import React, { useState } from 'react';
import { Plus, X, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../lib/api';
import { usePublicSettings, patchPublicSettings } from '../hooks/usePublicSettings';
import { useEditMode } from './InlineEditable';

/**
 * Admin-editable list of strings, stored as a JSON array in a single setting.
 *
 * Props:
 *   settingKey    - Setting key that holds the JSON array (e.g. "sch_universite_criteria")
 *   defaultItems  - Fallback array shown when the setting is absent
 *   renderItem    - (text, index) => ReactNode — how each row renders read-only
 *   addLabel      - "+ Yeni ekle" button label
 *   className     - Optional wrapper class
 *   listAs        - "ul" (default) or "div"
 *   itemClassName - Class applied to each row wrapper (only in read-only)
 *   emptyLabel    - Text shown when list is empty and edit mode is off
 */
export default function EditableList({
  settingKey,
  defaultItems = [],
  renderItem,
  addLabel = '+ Yeni ekle',
  className = '',
  listAs = 'ul',
  itemClassName = '',
  emptyLabel = '',
}) {
  const canEdit = useEditMode();
  const settings = usePublicSettings();

  // Setting value may be array (already parsed) or JSON string
  const raw = settings[settingKey];
  let items;
  if (Array.isArray(raw)) items = raw;
  else if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      items = Array.isArray(parsed) ? parsed : defaultItems;
    } catch {
      items = defaultItems;
    }
  } else {
    items = defaultItems;
  }

  const saveList = async (nextItems) => {
    try {
      await api.adminUpsertSetting(settingKey, nextItems);
      patchPublicSettings({ [settingKey]: nextItems });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Kaydedilemedi');
      throw e;
    }
  };

  const addItem = async () => {
    const text = window.prompt('Yeni öğe metni:', '');
    if (text === null) return;
    const clean = text.trim();
    if (!clean) return;
    try {
      await saveList([...items, clean]);
      toast.success('Eklendi');
    } catch (_e) { /* toast already shown in saveList */ }
  };

  const editItem = async (idx) => {
    const text = window.prompt('Metni düzenle:', items[idx]);
    if (text === null) return;
    const clean = text.trim();
    if (!clean) return;
    const next = [...items];
    next[idx] = clean;
    try {
      await saveList(next);
      toast.success('Güncellendi');
    } catch (_e) { /* toast already shown in saveList */ }
  };

  const removeItem = async (idx) => {
    if (!window.confirm(`"${items[idx]}" silinsin mi?`)) return;
    const next = items.filter((_, i) => i !== idx);
    try {
      await saveList(next);
      toast.success('Silindi');
    } catch (_e) { /* toast already shown in saveList */ }
  };

  if (items.length === 0 && !canEdit) {
    return emptyLabel ? <div className="text-ink-400 text-sm italic">{emptyLabel}</div> : null;
  }

  const Wrapper = listAs === 'ul' ? 'ul' : 'div';

  return (
    <Wrapper className={className} data-testid={`editable-list-${settingKey}`}>
      {items.map((text, i) => (
        <div key={i} className={`relative group ${itemClassName}`} data-testid={`editable-item-${settingKey}-${i}`}>
          {renderItem(text, i)}
          {canEdit && (
            <div className="absolute top-0 -right-1 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
              <button
                type="button"
                onClick={() => editItem(i)}
                title="Metni düzenle"
                data-testid={`editable-edit-${settingKey}-${i}`}
                className="w-7 h-7 grid place-items-center rounded-lg bg-ink-600 text-cream hover:bg-ink-500 shadow-md"
              >
                <Pencil className="w-3 h-3" />
              </button>
              <button
                type="button"
                onClick={() => removeItem(i)}
                title="Sil"
                data-testid={`editable-remove-${settingKey}-${i}`}
                className="w-7 h-7 grid place-items-center rounded-lg bg-coral-300 text-white hover:bg-coral-400 shadow-md"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          )}
        </div>
      ))}
      {canEdit && (
        <li className="list-none mt-4">
          <button
            type="button"
            onClick={addItem}
            data-testid={`editable-add-${settingKey}`}
            className="inline-flex items-center gap-1.5 text-xs h-9 px-4 rounded-full bg-ink-600 text-cream font-semibold hover:bg-ink-500"
          >
            <Plus className="w-3.5 h-3.5" /> {addLabel}
          </button>
        </li>
      )}
    </Wrapper>
  );
}
